import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, ChakraProvider } from "@chakra-ui/react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import BottomNav from "./components/BottomNav";
import ScrollToTop from "./components/ScrollToTop";
import HeroSection from "./components/HeroSection";
import Testimonials from "./components/Testimonials";
import DentalServices from "./components/Services";
import InfoSection from "./components/InfoSection";
import Visit from "./components/Visit";
import BookingSection from "./components/Booking";
import BlogList from "./components/Blog/ListBlog";
import BlogDetails from "./components/Blog/BlogDetails";
import DentalFilling from "./components/Services/DentalFillings";
import WisdomTeethRemoval from "./components/Services/WisdomTeethRemoval";
import RootCanalTreatment from "./components/Services/RootCanalTreatment";
import BracesAndAligners from "./components/Services/BracesAndAligners";
import DentalImplants from "./components/Services/DentalImplants";
import Dentures from "./components/Services/Dentures";
import BridgesAndCrowns from "./components/Services/BridgesAndCrowns";
import KidsDentistry from "./components/Services/KidsDentistry";
import MouthUlcers from "./components/Services/MouthUlcers";
import NotFound from "./components/NotFound";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import ScrollPage from "./components/ScrollPage";
import Tour from "./components/Tour";
import HomeFaq, { HOME_FAQS } from "./components/HomeFaq";
import Seo from "./seo/Seo";
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from "./seo/siteConfig";

// Only the admin screens are code-split.
//
// Splitting the public routes as well was measurably worse: because the app
// renders rather than hydrates, React clears the prerendered markup, paints the
// Suspense fallback while the route's chunk resolves, then paints the content —
// 0.19 CLS on mobile for every treatment page. Neither rel=preload nor emitting
// the chunk ahead of the runtime avoided that paint. The ~25 kB those routes add
// to the main bundle is the cheaper side of that trade, since CLS is a Core Web
// Vital and the treatment pages are the ones that need to rank.
//
// The admin screens are safe to split: staff-only, never prerendered, and
// nobody's Core Web Vitals depend on them.
const AdminLoginPage = React.lazy(() => import("./components/admin/adminLogin"));
const AdminDashboard = React.lazy(() =>
  import("./components/admin/adminDashboard")
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Scroll to top on reload
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // Adjust if you prefer smooth scrolling
    });
  }, []);

  // Load Google Analytics script
  useEffect(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-3DKPYM38ZY";
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      gtag("js", new Date());
      gtag("config", "G-3DKPYM38ZY");
    };
  }, []);

  return (
    <ChakraProvider>
      <Router>
        <ScrollPage />
        <ScrollToTop /> {/* Ensure this is placed here */}
        <Header />
        {/* One boundary for every split route. The fallback reserves the same
            minimum height the routes occupy, so swapping it for real content
            does not shift the page. */}
        <Suspense fallback={<Box minH="100vh" bg="#fceee5" />}>
          <Routes>
          <Route path="/blog" element={<BlogList />} />
          <Route path="/dental-filling" element={<DentalFilling />} />
          <Route
            path="/wisdom-teeth-removal"
            element={<WisdomTeethRemoval />}
          />
          <Route
            path="/root-canal-treatment"
            element={<RootCanalTreatment />}
          />
          <Route path="/braces-and-aligners" element={<BracesAndAligners />} />
          <Route path="/dental-implants" element={<DentalImplants />} />
          <Route path="/dentures" element={<Dentures />} />
          <Route path="/bridges-and-crowns" element={<BridgesAndCrowns />} />
          <Route path="/kids-dentistry" element={<KidsDentistry />} />
          <Route path="/mouth-ulcers" element={<MouthUlcers />} />
          <Route
            path="/admin-login"
            element={
              <AdminLoginPage setIsAuthenticated={setIsAuthenticated} />
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/blogs/:id" element={<BlogDetails />} />
          <Route
            path="/"
            element={
              <>
                <Seo
                  path="/"
                  title={DEFAULT_TITLE}
                  description={DEFAULT_DESCRIPTION}
                  faqs={HOME_FAQS}
                />
                <HeroSection />
                <DentalServices />
                <Testimonials />
                <Tour />
                <InfoSection />
                <Visit />
                <HomeFaq />
                <BookingSection />
              </>
            }
          />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
        <BottomNav />
      </Router>
    </ChakraProvider>
  );
}

export default App;
